<template>
  <div class="cnfltd-results-table" data-testid="search-results-table">
    <div v-if="isMobile" class="cnfltd-results-mobile-grid">
      <div 
        v-for="record in sortedResults" 
        :key="record.domain" 
        class="cnfltd-mobile-card" 
        :class="getRowClassName(record)"
      >
        <div class="cnfltd-mobile-domain">{{ record.domain }}</div>
        
        <div class="cnfltd-mobile-price" v-if="record.available && record.price != null">
          <span class="cnfltd-mobile-currency">{{ currencySymbol }}</span>
          <span class="cnfltd-mobile-amount">{{ formatPrice(record.price) }}</span>
        </div>
        <div class="cnfltd-mobile-status" v-else>
          {{ record.available ? 'Available' : 'Taken' }}
        </div>

        <div class="cnfltd-mobile-actions">
          <a-button
            v-if="record.available && record.price != null"
            type="primary"
            size="small"
            class="cnfltd-mobile-add-button"
            :loading="isAddingDomain(record.domain)"
            :disabled="isAddingDomain(record.domain)"
            @click="handleAddToCart(record)"
          >
            <ShoppingCartOutlined v-if="!isAddingDomain(record.domain)" />
            Add
          </a-button>
          <span v-else class="cnfltd-mobile-unavailable">
            {{ record.available ? 'No pricing' : 'Taken' }}
          </span>
        </div>
      </div>
    </div>

    <a-table
      v-else
      :columns="columns"
      :data-source="sortedResults"
      :pagination="false"
      row-key="domain"
      :row-class-name="getRowClassName"
    >
      <!-- Domain Column -->
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'domain'">
          <div class="cnfltd-domain-cell">
            <span class="cnfltd-domain-name">{{ record.domain }}</span>
          </div>
        </template>

        <!-- Status Column -->
        <template v-else-if="column.key === 'status'">
          <a-tag 
            :color="record.available ? 'success' : 'default'"
            class="cnfltd-status-tag"
          >
            {{ record.available ? 'Available' : 'Taken' }}
          </a-tag>
        </template>

        <!-- Price Column -->
        <template v-else-if="column.key === 'price'">
          <div v-if="record.available && record.price != null" class="cnfltd-price-cell">
            <span class="cnfltd-currency">{{ currencySymbol }}</span>
            <span class="cnfltd-amount">{{ formatPrice(record.price) }}</span>
            <span class="cnfltd-period">/year</span>
          </div>
          <span v-else class="cnfltd-no-price">-</span>
        </template>

        <!-- Action Column -->
        <template v-else-if="column.key === 'action'">
          <a-button
            v-if="record.available && record.price != null"
            type="primary"
            size="small"
            class="cnfltd-add-button"
            :loading="isAddingDomain(record.domain)"
            :disabled="isAddingDomain(record.domain)"
            @click="handleAddToCart(record)"
          >
            <ShoppingCartOutlined v-if="!isAddingDomain(record.domain)" />
            {{ isAddingDomain(record.domain) ? 'Adding...' : 'Add to Cart' }}
          </a-button>
          <span v-else-if="!record.available" class="cnfltd-unavailable-text">Unavailable</span>
          <span v-else-if="record.available && record.price == null" class="cnfltd-no-pricing-text">No pricing</span>
        </template>
      </template>
    </a-table>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { ShoppingCartOutlined } from '@ant-design/icons-vue'
import type { DomainSearchResult } from '../types/config'

// Props
interface Props {
  results: DomainSearchResult[]
  currencySymbol?: string
  addingDomain?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  currencySymbol: 'R',
  addingDomain: null
})

// Mobile detection
const windowWidth = ref(window.innerWidth)
const updateWidth = () => {
  windowWidth.value = window.innerWidth
}

onMounted(() => {
  window.addEventListener('resize', updateWidth)
})

onUnmounted(() => {
  window.removeEventListener('resize', updateWidth)
})

const isMobile = computed(() => windowWidth.value <= 768)

// Emits
const emit = defineEmits<{
  addToCart: [domain: string, price: number]
}>()

// Table columns
const columns = [
  {
    title: 'Domain',
    key: 'domain',
    dataIndex: 'domain',
    width: '40%',
  },
  {
    title: 'Status',
    key: 'status',
    width: '20%',
    align: 'center' as const,
  },
  {
    title: 'Price',
    key: 'price',
    width: '20%',
    align: 'right' as const,
  },
  {
    title: 'Action',
    key: 'action',
    width: '20%',
    align: 'center' as const,
  },
]

// Sort results: available domains first
const sortedResults = computed(() => {
  return [...props.results].sort((a, b) => {
    if (a.available && !b.available) return -1
    if (!a.available && b.available) return 1
    return 0
  })
})

// Row class name based on availability
const getRowClassName = (record: DomainSearchResult) => {
  return record.available ? 'cnfltd-row-available' : 'cnfltd-row-unavailable'
}

// Format price with 2 decimal places
const formatPrice = (price?: number): string => {
  if (price === undefined) return '0.00'
  return price.toFixed(2)
}

// Check if a specific domain is being added to cart
const isAddingDomain = (domain: string): boolean => {
  return props.addingDomain === domain
}

// Handle add to cart
const handleAddToCart = (record: DomainSearchResult) => {
  console.log('[ResultsTable] handleAddToCart called with record:', record)
  console.log('[ResultsTable] Price value:', record.price, 'Type:', typeof record.price)

  if (record.available && record.price != null) {
    console.log('[ResultsTable] Emitting addToCart with domain:', record.domain, 'price:', record.price)
    emit('addToCart', record.domain, record.price)
  } else {
    console.warn('[ResultsTable] Not emitting - available:', record.available, 'price:', record.price)
  }
}
</script>

<style scoped>
.cnfltd-results-table {
  margin-top: 2rem;
  background: transparent;
}

/* Mobile List Styling */
.cnfltd-results-mobile-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  width: 100%;
}

.cnfltd-mobile-card {
  background: #ffffff;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid #f0f0f0;
  transition: transform 0.2s;
}

.cnfltd-mobile-card:active {
  transform: scale(0.98);
}

.cnfltd-mobile-domain {
  font-weight: 700;
  font-size: 1.25rem;
  color: #262626;
  margin-bottom: 8px;
  word-break: break-all;
}

.cnfltd-mobile-price {
  margin-bottom: 12px;
}

.cnfltd-mobile-currency {
  font-size: 0.8rem;
  color: #a4c639;
  font-weight: 600;
  margin-right: 2px;
}

.cnfltd-mobile-amount {
  font-size: 1rem;
  font-weight: 700;
  color: #a4c639;
}

.cnfltd-mobile-status {
  font-size: 0.9rem;
  color: #8c8c8c;
  margin-bottom: 12px;
}

.cnfltd-mobile-actions {
  margin-top: auto;
  width: 100%;
}

.cnfltd-mobile-add-button {
  width: 100%;
  border-radius: 6px !important;
  height: 36px !important;
  background: #a4c639 !important;
  border-color: #a4c639 !important;
  font-weight: 600;
}

.cnfltd-mobile-unavailable {
  font-size: 0.8rem;
  color: #bfbfbf;
  font-style: italic;
}

/* Row styling */
:deep(.cnfltd-row-available) {
  background-color: #f6ffed;
}

:deep(.cnfltd-row-available:hover) {
  background-color: #eaffd9 !important;
}

:deep(.cnfltd-row-unavailable) {
  background-color: #fafafa;
  opacity: 0.7;
}

:deep(.cnfltd-row-unavailable:hover) {
  background-color: #f5f5f5 !important;
}

/* Domain cell */
.cnfltd-domain-cell {
  display: flex;
  align-items: center;
}

.cnfltd-domain-name {
  font-weight: 600;
  font-size: 1rem;
  color: #333;
}

/* Status tag */
.cnfltd-status-tag {
  font-weight: 600;
  padding: 2px 12px;
  border-radius: 4px;
}

/* Price cell */
.cnfltd-price-cell {
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 0.25rem;
}

.cnfltd-currency {
  font-size: 0.875rem;
  color: #666;
}

.cnfltd-amount {
  font-size: 1.125rem;
  font-weight: 700;
  color: #52c41a;
}

.cnfltd-period {
  font-size: 0.75rem;
  color: #999;
}

.cnfltd-no-price {
  color: #999;
  font-size: 0.875rem;
}

/* Action button */
.cnfltd-add-button {
  background: #a4c639 !important;
  border-color: #a4c639 !important;
  font-weight: 600;
  font-size: 0.875rem;
}

.cnfltd-add-button:hover {
  background: #8fb02d !important;
  border-color: #8fb02d !important;
}

.cnfltd-unavailable-text {
  color: #999;
  font-size: 0.875rem;
  font-style: italic;
}

.cnfltd-no-pricing-text {
  color: #ff9800;
  font-size: 0.875rem;
  font-style: italic;
  font-weight: 500;
}

/* Table header styling */
:deep(.ant-table-thead > tr > th) {
  background: #fafafa;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #e0e0e0;
}

/* Table cell padding */
:deep(.ant-table-tbody > tr > td) {
  padding: 16px;
}
</style>
